import React, { useCallback } from 'react';
import { AThroughZMobileFilterCategory } from 'features/search/components/AlphabetFilterUtils';
import { BonusToggle } from 'components/bonus-toggle/BonusToggle';
import { Btn } from 'components/btn/Btn';
import { sortOptions } from './state';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { categoryIcons } from 'features/search/components/utils';

interface DealsFilterSectionProps {
    categories: string[];
    state: any;
    dispatch: any;
    count: number;
}

const DealsFilterSection = ({
    categories = [],
    state,
    dispatch,
    count,
}: DealsFilterSectionProps) => {
    const onPanelDealTypeClick = useCallback(
        (dealType: string) => {
            const nextDeals = state.panelTempOptions.dealTypes.includes(dealType)
                ? state.panelTempOptions.dealTypes.filter((dt: string) => dt !== dealType)
                : [...state.panelTempOptions.dealTypes, dealType];

            dispatch({
                type: 'UPDATE_TEMP_PANEL_OPTIONS',
                payload: {
                    dealTypes: nextDeals,
                },
            });
        },
        [state, dispatch]
    );

    const onPanelCategoryClick = useCallback(
        (category: string) => {
            const nextCategories = state.panelTempOptions.categories.includes(category)
                ? state.panelTempOptions.categories.filter((c: string) => c !== category)
                : [...state.panelTempOptions.categories, category];

            dispatch({
                type: 'UPDATE_TEMP_PANEL_OPTIONS',
                payload: {
                    categories: nextCategories,
                },
            });
        },
        [state, dispatch]
    );

    return (
        <div className="lg:w-[1024px] xl:w-[1280px] 2xl:w-[1440px] md:mx-auto w-full px-2">
            <div className="w-full flex justify-between items-center">
                <div className="py-8">
                    <span className="text-brand-dark font-semibold">Filter &amp; Sort:&nbsp;</span>
                    {count > 0 && <span>{count} Results</span>}
                </div>

                {state.showSortByPanel && (
                    <div
                        className="fixed top-0 bottom-0 left-0 right-0"
                        onClick={() => dispatch({ type: 'SET_SHOW_SORT_BY_PANEL', payload: false })}
                    />
                )}

                <div className="relative flex flex-row justify-end">
                    <span>Sort&nbsp;</span>
                    <FontAwesomeIcon
                        className="mx-2 text-[7px] self-center"
                        icon={faCircle}
                        size="xs"
                    />

                    <button
                        className="underline text-brand"
                        onClick={() => {
                            dispatch({
                                type: 'SET_SHOW_SORT_BY_PANEL',
                                payload: !state.showSortByPanel,
                            });
                        }}
                    >
                        {state.sortBy.label}
                    </button>

                    {state.showSortByPanel && (
                        <div className="absolute z-50 bg-white p-2 top-[100%] w-[220px] px-[20px] py-[10px] shadow">
                            {sortOptions.map((option) => (
                                <div key={option.value} className="flex items-center py-2">
                                    <input
                                        type="radio"
                                        id={option.value}
                                        value={option.value}
                                        checked={state.sortBy.value === option.value}
                                        onChange={() =>
                                            dispatch({ type: 'SET_SORT_BY', payload: option })
                                        }
                                    />
                                    <label className="ml-2 text-brand" htmlFor={option.value}>
                                        {option.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <AThroughZMobileFilterCategory
                currentFilter={state.letterFilter}
                onFilterClicked={(letter) => {
                    dispatch({ type: 'SET_LETTER_FILTER', payload: letter });
                }}
                noGrid
                hideRecommended
            />

            <div className="w-full border-t border-b flex justify-around items-center relative">
                <button
                    className={[
                        'm-2',
                        state.showPanel === 'dealTypes'
                            ? 'text-dark-blue text-semibold border-b-[3px] border-b-bonus-pink'
                            : 'underline text-brand',
                    ].join(' ')}
                    onClick={() => dispatch({ type: 'SET_SHOW_PANEL', payload: 'dealTypes' })}
                >
                    Deal Type
                </button>

                <button
                    className={[
                        'm-2',
                        state.showPanel === 'categories'
                            ? 'text-dark-blue text-semibold border-b-[3px] border-b-bonus-pink'
                            : 'underline text-brand',
                    ].join(' ')}
                    onClick={() => dispatch({ type: 'SET_SHOW_PANEL', payload: 'categories' })}
                >
                    Categories
                </button>

                {state.showPanel !== '' && (
                    <div className="absolute left-0 right-0 top-[100%] min-h-[416px] bg-white z-10 flex flex-col shadow">
                        {state.showPanel == 'dealTypes' && (
                            <div className="flex flex-row justify-around items-center w-full flex-grow">
                                {['Online Coupon', 'Online Deal'].map((dealType) => (
                                    <button
                                        key={dealType}
                                        className={[
                                            'px-[60px] py-[8px] border border-dark-blue rounded-[8px]',
                                            state.panelTempOptions.dealTypes.includes(dealType)
                                                ? 'text-dark-blue bg-pale-blue'
                                                : 'text-brand',
                                        ].join(' ')}
                                        onClick={() => {
                                            onPanelDealTypeClick(dealType);
                                        }}
                                    >
                                        {dealType}
                                    </button>
                                ))}
                            </div>
                        )}

                        {state.showPanel == 'categories' && (
                            <div className="grid lg:grid-cols-4 grid-cols-2 gap-4 w-full flex-grow px-2 py-4">
                                {categories.map((category) => {
                                    const Icon = categoryIcons[category];

                                    return (
                                        <button
                                            key={category}
                                            className={[
                                                'pl-[4px] py-[8px] border border-dark-blue rounded-[8px] text-[11px] text-left flex flex-row justify-start items-center gap-2',
                                                state.panelTempOptions.categories.includes(category)
                                                    ? 'text-dark-blue bg-pale-blue'
                                                    : 'text-brand',
                                                !Icon && 'justify-center',
                                            ].join(' ')}
                                            onClick={() => {
                                                onPanelCategoryClick(category);
                                            }}
                                        >
                                            {Icon && <Icon className="h-8" />}

                                            {category}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        <div className="flex flex-row justify-center gap-2 items-center w-full">
                            <Btn
                                onClick={() => {
                                    dispatch({ type: 'CANCEL_PANEL' });
                                }}
                                isSecondary
                            >
                                Cancel
                            </Btn>

                            <Btn
                                onClick={() => {
                                    dispatch({ type: 'CONFIRM_PANEL' });
                                }}
                            >
                                Show Results
                            </Btn>
                        </div>

                        <div className="flex flex-row justify-end px-2">
                            <BonusToggle
                                currentBonusState={state.panelTempOptions.viewBonusOnly}
                                toggleFunc={() => {
                                    dispatch({
                                        type: 'UPDATE_TEMP_PANEL_OPTIONS',
                                        payload: {
                                            ...state.panelTempOptions,
                                            viewBonusOnly: !state.panelTempOptions.viewBonusOnly,
                                        },
                                    });
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className="w-full flex flex-row flex-wrap align-baseline mt-2">
                {(state.dealTypes.length > 0 || state.categories.length > 0) && (
                    <button
                        className="font-semibold text-brand text-[12px] pr-2 underline"
                        onClick={() => {
                            dispatch({ type: 'CLEAR_ALL_CATEGORIES' });
                            dispatch({ type: 'CLEAR_ALL_DEAL_TYPES' });
                        }}
                    >
                        Clear All
                    </button>
                )}

                <ul className="flex flex-row flex-wrap">
                    {state.dealTypes.map((dealType: string) => (
                        <li key={dealType}>
                            <button
                                className="font-semibold text-brand text-[12px] mr-2 rounded-full px-2 py-1 bg-pale-blue mb-1"
                                onClick={() => {
                                    dispatch({
                                        type: 'REMOVE_DEAL_TYPE',
                                        payload: dealType,
                                    });
                                }}
                            >
                                <span className="mr-1">{dealType}</span>
                                <FontAwesomeIcon
                                    icon={faTimesCircle}
                                    className="text-brand-dark inset-0 m-auto"
                                />
                            </button>
                        </li>
                    ))}
                    {state.categories.map((category: string) => (
                        <li key={category}>
                            <button
                                className="font-semibold text-brand text-[12px] mr-2 rounded-full px-2 py-1 bg-pale-blue mb-1"
                                onClick={() => {
                                    dispatch({
                                        type: 'REMOVE_CATEGORY',
                                        payload: category,
                                    });
                                }}
                            >
                                <span className="mr-1">{category}</span>
                                <FontAwesomeIcon
                                    icon={faTimesCircle}
                                    className="text-brand-dark inset-0 m-auto"
                                />
                            </button>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="w-full flex justify-end pt-2">
                <BonusToggle
                    currentBonusState={state.viewBonusOnly}
                    toggleFunc={() => {
                        dispatch({ type: 'SET_VIEW_BONUS_ONLY', payload: !state.viewBonusOnly });
                    }}
                />
            </div>

            {/* <pre className="w-full text-xs">{JSON.stringify(state, null, 2)}</pre> */}
        </div>
    );
};

export default DealsFilterSection;
